'use client';

import NextImage, { ImageProps } from 'next/image';
import { useState } from 'react';

type Props = Omit<ImageProps, 'onLoad'> & {
  shimmerColor?: string;
};

export default function BlurImage({ shimmerColor, className = '', style, fill, width, height, ...props }: Props) {
  const [loaded, setLoaded] = useState(false);

  const base = shimmerColor ?? '#cbc5b4';
  const shimmerStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    zIndex: 1,
    pointerEvents: 'none',
    borderRadius: 'inherit',
    opacity: loaded ? 0 : 1,
    transition: 'opacity 0.5s ease',
    background: `linear-gradient(90deg, ${base} 0%, #ede8de 50%, ${base} 100%)`,
    backgroundSize: '200% 100%',
    // Stop the animation once the image has loaded — saves continuous GPU work
    animation: loaded ? 'none' : 'shimmer 1.6s ease-in-out infinite',
  };

  if (fill) {
    return (
      <div style={{ position: 'absolute', inset: 0 }}>
        {/* Priority images fire onLoad during React hydration (already loaded via preload).
            suppressHydrationWarning lets React accept the opacity mismatch without error #418. */}
        <span suppressHydrationWarning aria-hidden="true" style={shimmerStyle} />
        <NextImage
          suppressHydrationWarning
          {...props}
          fill
          className={className}
          style={{ ...style, opacity: loaded ? 1 : 0, transition: 'opacity 0.3s ease, transform 0.3s ease' }}
          onLoad={() => setLoaded(true)}
        />
      </div>
    );
  }

  return (
    <span style={{ position: 'relative', display: 'inline-block', lineHeight: 0 }}>
      <span suppressHydrationWarning aria-hidden="true" style={{ ...shimmerStyle, borderRadius: 0 }} />
      <NextImage
        suppressHydrationWarning
        {...props}
        width={width}
        height={height}
        className={className}
        style={{ ...style, opacity: loaded ? 1 : 0, transition: 'opacity 0.3s ease, transform 0.3s ease', display: 'block' }}
        onLoad={() => setLoaded(true)}
      />
    </span>
  );
}
